import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card } from './ui/card';
import VillaCard from './VillaCard';
import EmotionalFilters from './EmotionalFilters';
import { 
  Search, 
  SlidersHorizontal, 
  MapPin, 
  Calendar, 
  Users, 
  Euro,
  Grid3X3,
  List,
  Star,
  Heart,
  ChevronDown,
  X,
  Loader2
} from 'lucide-react';
import { villasService, filteringRulesService } from '../services/api';
import { destinationRegions } from '../data/destinations';

const SearchPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [filters, setFilters] = useState({
    destination: searchParams.get('destination') || '',
    zones: [], // Changé en tableau pour sélection multiple
    checkIn: searchParams.get('checkIn') || '',
    checkOut: searchParams.get('checkOut') || '',
    guests: searchParams.get('guests') ? parseInt(searchParams.get('guests')) : '',
    minPrice: '',
    maxPrice: '',
    bedrooms: '',
    emotion: null,
    amenities: []
  });
  
  const [viewMode, setViewMode] = useState('grid');
  const [sortBy, setSortBy] = useState('relevance');
  const [showFilters, setShowFilters] = useState(false);
  const [favorites, setFavorites] = useState(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const villasPerPage = 50;
  
  // États pour les données de l'API
  const [villas, setVillas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [countries, setCountries] = useState([]);
  const [allZones, setAllZones] = useState([]);
  const [availableZones, setAvailableZones] = useState([]);
  const [totalVillas, setTotalVillas] = useState(0);
  const [isFiltered, setIsFiltered] = useState(false);
  const [availabilityStatus, setAvailabilityStatus] = useState({}); // { villaId: 'available' | 'unavailable' | 'unknown' }
  const [checkingAvailability, setCheckingAvailability] = useState(false);

  // Get all destinations for dropdown
  const allDestinations = Object.values(destinationRegions).flatMap(region => 
    region.destinations.map(dest => ({ ...dest, region: region.name }))
  );

  // Charger les pays et zones depuis l'API
  useEffect(() => {
    const fetchDestinations = async () => {
      try {
        const response = await filteringRulesService.getDestinations();
        if (response.success && response.data) {
          setCountries(response.data.countries || []);
          setAllZones(response.data.zones || []);
        }
      } catch (err) {
        console.error('Error loading destinations:', err);
      }
    };
    
    fetchDestinations();
  }, []);

  // Filtrer les zones selon le pays sélectionné
  useEffect(() => {
    if (filters.destination) {
      const zonesForCountry = allZones.filter(z => z.country === filters.destination);
      setAvailableZones(zonesForCountry);
    } else {
      setAvailableZones([]);
      // Réinitialiser les zones si aucun pays n'est sélectionné
      if (filters.zones.length > 0) {
        setFilters(prev => ({ ...prev, zones: [] }));
      }
    }
  }, [filters.destination, allZones]);

  // Charger les villas depuis l'API
  useEffect(() => {
    const fetchVillas = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const apiFilters = {
          country: filters.destination || undefined,
          zone: filters.zones.length > 0 ? filters.zones.join(',') : undefined,
          min_bedrooms: filters.bedrooms || undefined,
          min_pax: filters.guests || undefined,
          limit: 10000 // Charger toutes les villas (limite haute pour récupérer tout)
        };
        
        const response = await villasService.getVillas(apiFilters);
        
        if (response.success && response.data) {
          // Convertir les villas MySQL au format attendu par VillaCard
          const formattedVillas = response.data.map(villa => ({
            id: villa.id,
            name: villa.public_name,
            location: `${villa.city}, ${villa.country}`,
            bedrooms: parseInt(villa.number_bedrooms) || 0,
            bathrooms: parseInt(villa.number_bathrooms) || 0,
            capacity: villa.number_pax ? `${villa.number_pax}` : '4',
            priceDisplay: (villa.min_price && parseFloat(villa.min_price) > 0) ? `${Math.round(villa.min_price).toLocaleString('fr-FR')} €` : 'Sur demande',
            priceSubtext: (villa.min_price && parseFloat(villa.min_price) > 0) ? 'par nuit' : '',
            price: villa.min_price || 0,
            mainImage: villa.main_photo || 'https://images.unsplash.com/photo-1613490493576-7fde63acd811?w=800',
            image: villa.main_photo || 'https://images.unsplash.com/photo-1613490493576-7fde63acd811?w=800',
            emotionalTagline: villa.short_description ? villa.short_description.substring(0, 100) + '...' : 'Villa de luxe avec vue exceptionnelle',
            propertyType: villa.type_of_house || 'Villa',
            zone: villa.zone || villa.city,
            rating: 4.8,
            reviews: Math.floor(Math.random() * 50) + 10
          }));
          
          setVillas(formattedVillas);
          setTotalVillas(response.total || formattedVillas.length);
          setIsFiltered(response.filtered || false);
        }
      } catch (err) {
        console.error('Error loading villas:', err);
        setError('Erreur lors du chargement des villas');
      } finally {
        setLoading(false);
      }
    };
    
    fetchVillas();
  }, [filters.destination, filters.zones, filters.bedrooms, filters.guests]);

  // Réinitialiser les disponibilités quand les dates ou les filtres changent
  useEffect(() => {
    setAvailabilityStatus({});
  }, [filters.checkIn, filters.checkOut, filters.destination, filters.zones, currentPage]);

  // Filter villas based on remaining criteria
  const filteredVillas = villas.filter(villa => {
    const matchesEmotion = !filters.emotion || villa.propertyType === filters.emotion;
    return matchesEmotion;
  });

  // Sort filtered villas
  const sortedVillas = [...filteredVillas].sort((a, b) => {
    switch (sortBy) {
      case 'price-low':
        return a.price - b.price;
      case 'price-high':
        return b.price - a.price;
      case 'name':
        return a.name.localeCompare(b.name);
      default:
        return 0;
    }
  });

  // Pagination
  const totalPages = Math.ceil(sortedVillas.length / villasPerPage);
  const startIndex = (currentPage - 1) * villasPerPage;
  const endIndex = startIndex + villasPerPage;
  const paginatedVillas = sortedVillas.slice(startIndex, endIndex);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters.destination, filters.zones, filters.bedrooms, filters.guests, filters.emotion, sortBy]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  // Gérer la sélection/déselection des zones (multi-sélection)
  const handleZoneToggle = (zone) => {
    setFilters(prev => {
      const isSelected = prev.zones.includes(zone);
      if (isSelected) {
        // Retirer la zone
        return { ...prev, zones: prev.zones.filter(z => z !== zone) };
      } else {
        // Ajouter la zone
        return { ...prev, zones: [...prev.zones, zone] };
      }
    });
  };

  // Sélectionner toutes les zones
  const handleSelectAllZones = () => {
    const allZoneNames = availableZones.map(z => z.zone);
    setFilters(prev => ({ ...prev, zones: allZoneNames }));
  };

  // Désélectionner toutes les zones
  const handleClearAllZones = () => {
    setFilters(prev => ({ ...prev, zones: [] }));
  };

  const handleToggleFavorite = (villaId) => {
    setFavorites(prev => {
      const newFavorites = new Set(prev);
      if (newFavorites.has(villaId)) {
        newFavorites.delete(villaId);
      } else {
        newFavorites.add(villaId);
      }
      return newFavorites;
    });
  };

  const handleViewVilla = (villa) => {
    navigate(`/villa/${villa.id}`);
  };

  // Vérifier les disponibilités pour les villas affichées
  const checkAvailabilities = async () => {
    if (!filters.checkIn || !filters.checkOut) {
      alert('Veuillez sélectionner des dates d\'arrivée et de départ');
      return;
    }

    setCheckingAvailability(true);
    const newAvailabilityStatus = {};

    try {
      // Vérifier les disponibilités pour les villas de la page actuelle
      const promises = paginatedVillas.map(async (villa) => {
        try {
          const response = await villasService.getVillaAvailability(villa.id);
          
          if (response.success && response.data && response.data.icalData) {
            // Parser les données iCal
            const { getBookedDates } = await import('../utils/icalParser');
            const bookedDates = getBookedDates(response.data.icalData);
            
            // Vérifier si les dates sont disponibles
            const checkInDate = new Date(filters.checkIn);
            const checkOutDate = new Date(filters.checkOut);
            
            const isAvailable = !bookedDates.some(booking => {
              const bookingStart = new Date(booking.start);
              const bookingEnd = new Date(booking.end);
              
              // Vérifie s'il y a un chevauchement entre les dates demandées et les réservations
              return (checkInDate < bookingEnd && checkOutDate > bookingStart);
            });
            
            newAvailabilityStatus[villa.id] = isAvailable ? 'available' : 'unavailable';
          } else {
            newAvailabilityStatus[villa.id] = 'unknown';
          }
        } catch (error) {
          console.error(`Error checking availability for villa ${villa.id}:`, error);
          newAvailabilityStatus[villa.id] = 'unknown';
        }
      });

      await Promise.all(promises);
      setAvailabilityStatus(newAvailabilityStatus);
    } catch (error) {
      console.error('Error checking availabilities:', error);
      alert('Erreur lors de la vérification des disponibilités');
    } finally {
      setCheckingAvailability(false);
    }
  };

  const clearFilters = () => {
    setFilters({
      destination: '',
      zones: [],
      checkIn: '',
      checkOut: '',
      guests: '',
      minPrice: '',
      maxPrice: '',
      bedrooms: '',
      emotion: null,
      amenities: []
    });
    setAvailabilityStatus({}); // Réinitialiser les disponibilités
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 pt-16">
      {/* Hero Section avec Photo */}
      <section className="relative h-[500px] md:h-[600px] overflow-hidden">
        {/* Image de Fond */}
        <div className="absolute inset-0">
          <img 
            src="https://www.villanovo.com/photos/7871/P1024788_Panorama_153.jpg" 
            alt="Villa de luxe avec vue panoramique"
            className="w-full h-full object-cover object-center"
          />
          {/* Overlay élégant pour le contraste */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70"></div>
        </div>

        {/* Contenu */}
        <div className="relative z-10 h-full flex items-center">
          <div className="max-w-7xl mx-auto px-6 w-full">
            <div className="text-center max-w-4xl mx-auto">
              {/* Badge */}
              <div className="inline-block mb-6">
                <span className="px-6 py-3 bg-white/10 backdrop-blur-md border border-white/20 rounded-full text-white text-sm font-light tracking-wide">
                  {villas.length} villas d'exception disponibles
                </span>
              </div>

              {/* Titre Principal */}
              <h1 
                className="text-5xl md:text-6xl lg:text-7xl font-light mb-6 text-white drop-shadow-2xl"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                Découvrez votre villa de rêve
              </h1>
              
              {/* Sous-titre */}
              <p className="text-xl md:text-2xl text-white/95 mb-10 font-light drop-shadow-lg leading-relaxed max-w-3xl mx-auto">
                Des propriétés d'exception dans les plus belles destinations du monde. 
                Laissez-vous inspirer par notre collection exclusive de villas de luxe.
              </p>
              
              {/* Stats */}
              <div className="flex flex-wrap justify-center gap-6 md:gap-8">
                <div className="bg-white/10 backdrop-blur-md px-6 py-4 rounded-2xl border border-white/20">
                  <div className="text-3xl md:text-4xl font-light text-amber-300 mb-1">1,159</div>
                  <div className="text-xs md:text-sm text-white/80 uppercase tracking-wider font-light">Villas</div>
                </div>
                <div className="bg-white/10 backdrop-blur-md px-6 py-4 rounded-2xl border border-white/20">
                  <div className="text-3xl md:text-4xl font-light text-amber-300 mb-1">24</div>
                  <div className="text-xs md:text-sm text-white/80 uppercase tracking-wider font-light">Destinations</div>
                </div>
                <div className="bg-white/10 backdrop-blur-md px-6 py-4 rounded-2xl border border-white/20">
                  <div className="text-3xl md:text-4xl font-light text-amber-300 mb-1">5★</div>
                  <div className="text-xs md:text-sm text-white/80 uppercase tracking-wider font-light">Service</div>
                </div>
                <div className="bg-white/10 backdrop-blur-md px-6 py-4 rounded-2xl border border-white/20">
                  <div className="text-3xl md:text-4xl font-light text-amber-300 mb-1">24/7</div>
                  <div className="text-xs md:text-sm text-white/80 uppercase tracking-wider font-light">Conciergerie</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20">
          <div className="flex flex-col items-center gap-2 text-white/90 animate-bounce">
            <span className="text-sm font-light tracking-wide drop-shadow-lg">Découvrir</span>
            <ChevronDown className="w-6 h-6" />
          </div>
        </div>
      </section>

      {/* Search Header */}
      <header className="bg-white border-b border-gray-100 sticky top-16 z-40 shadow-md">
        <div className="max-w-7xl mx-auto px-6 py-4">
          {/* Search Bar */}
          <div className="flex flex-col lg:flex-row gap-4 items-center">
            <div className="flex-1 grid grid-cols-1 md:grid-cols-4 gap-3 w-full">
              {/* Destination */}
              <div className="relative">
                <label className="text-xs text-gray-500 mb-1 block">Destination</label>
                <select
                  value={filters.destination}
                  onChange={(e) => handleFilterChange('destination', e.target.value)}
                  className="w-full p-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 bg-white"
                >
                  <option value="">Tous les pays</option>
                  {countries.map((country) => (
                    <option key={country} value={country}>
                      {country}
                    </option>
                  ))}
                </select>
              </div>

              {/* Check-in */}
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Arrivée</label>
                <input
                  type="date"
                  value={filters.checkIn}
                  onChange={(e) => handleFilterChange('checkIn', e.target.value)}
                  className="w-full p-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                />
              </div>

              {/* Check-out */}
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Départ</label>
                <input
                  type="date"
                  value={filters.checkOut}
                  onChange={(e) => handleFilterChange('checkOut', e.target.value)}
                  className="w-full p-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                />
              </div>

              {/* Guests */}
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Voyageurs</label>
                <select
                  value={filters.guests}
                  onChange={(e) => handleFilterChange('guests', e.target.value === '' ? '' : parseInt(e.target.value))}
                  className="w-full p-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                >
                  <option value="">Peu importe</option>
                  {[...Array(12)].map((_, i) => (
                    <option key={i + 1} value={i + 1}>
                      {i + 1} {i + 1 === 1 ? 'personne' : 'personnes'}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex gap-2">
              <Button
                onClick={() => setShowFilters(!showFilters)}
                variant="outline"
                className="flex items-center gap-2 px-4 py-3 border-gray-200"
              >
                <SlidersHorizontal className="w-4 h-4" />
                Filtres
              </Button>
              
              {/* Bouton de vérification des disponibilités */}
              {filters.checkIn && filters.checkOut && (
                <Button
                  onClick={checkAvailabilities}
                  disabled={checkingAvailability}
                  className="flex items-center gap-2 px-4 py-3 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white"
                >
                  {checkingAvailability ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Vérification...
                    </>
                  ) : (
                    <>
                      <Calendar className="w-4 h-4" />
                      Vérifier les disponibilités
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>

          {/* Zone Filter - Appears when a country is selected */}
          {filters.destination && availableZones.length > 0 && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm font-medium text-gray-700">
                  Zones en {filters.destination}
                  {filters.zones.length > 0 && (
                    <span className="ml-2 text-xs text-amber-600 font-normal">
                      ({filters.zones.length} sélectionnée{filters.zones.length > 1 ? 's' : ''})
                    </span>
                  )}
                </label>
                <div className="flex gap-2">
                  <button
                    onClick={handleSelectAllZones}
                    className="text-xs text-amber-600 hover:text-amber-700 font-medium"
                  >
                    Tout sélectionner
                  </button>
                  {filters.zones.length > 0 && (
                    <button
                      onClick={handleClearAllZones}
                      className="text-xs text-gray-500 hover:text-gray-700 font-medium"
                    >
                      Effacer
                    </button>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {availableZones.map((zoneObj) => {
                  const isSelected = filters.zones.includes(zoneObj.zone);
                  return (
                    <button
                      key={zoneObj.zone}
                      onClick={() => handleZoneToggle(zoneObj.zone)}
                      className={`px-4 py-2 border-2 rounded-lg cursor-pointer transition-all ${
                        isSelected
                          ? 'border-amber-500 bg-amber-50 text-amber-700'
                          : 'border-gray-300 hover:border-amber-300 bg-white text-gray-700'
                      }`}
                    >
                      {zoneObj.zone}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Advanced Filters */}
          {showFilters && (
            <Card className="mt-4 p-6 bg-white/80 backdrop-blur-sm">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                {/* Price Range */}
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">
                    Budget (€/semaine)
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="number"
                      placeholder="Min"
                      value={filters.minPrice}
                      onChange={(e) => handleFilterChange('minPrice', e.target.value)}
                      className="p-2 border border-gray-200 rounded focus:outline-none focus:ring-2 focus:ring-amber-500"
                    />
                    <input
                      type="number"
                      placeholder="Max"
                      value={filters.maxPrice}
                      onChange={(e) => handleFilterChange('maxPrice', e.target.value)}
                      className="p-2 border border-gray-200 rounded focus:outline-none focus:ring-2 focus:ring-amber-500"
                    />
                  </div>
                </div>

                {/* Bedrooms */}
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">
                    Chambres min.
                  </label>
                  <select
                    value={filters.bedrooms}
                    onChange={(e) => handleFilterChange('bedrooms', e.target.value)}
                    className="w-full p-2 border border-gray-200 rounded focus:outline-none focus:ring-2 focus:ring-amber-500"
                  >
                    <option value="">Indifférent</option>
                    {[1, 2, 3, 4, 5, 6].map(num => (
                      <option key={num} value={num}>{num}+ chambre{num > 1 ? 's' : ''}</option>
                    ))}
                  </select>
                </div>

                {/* Clear Filters */}
                <div className="flex items-end">
                  <Button 
                    variant="outline" 
                    onClick={clearFilters}
                    className="w-full"
                  >
                    Effacer les filtres
                  </Button>
                </div>
              </div>

              {/* Emotional Filters */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <label className="text-sm font-medium text-gray-700 mb-4 block">
                  Filtrer par émotion
                </label>
                <EmotionalFilters 
                  selectedEmotion={filters.emotion}
                  onEmotionChange={(emotion) => handleFilterChange('emotion', emotion)}
                />
              </div>
            </Card>
          )}
        </div>
      </header>

      {/* Results Section */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Results Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8">
          <div>
            <h1 
              className="text-3xl font-light text-gray-800 mb-2"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              {filters.zones.length > 0
                ? filters.zones.length === 1
                  ? `Villas à ${filters.zones[0]}, ${filters.destination}`
                  : `Villas en ${filters.destination} (${filters.zones.length} zones)`
                : filters.destination 
                  ? `Villas en ${filters.destination}` 
                  : 'Recherche de Villas'}
            </h1>
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-3">
                <p className="text-gray-600">
                  {sortedVillas.length} villa{sortedVillas.length > 1 ? 's' : ''} disponible{sortedVillas.length > 1 ? 's' : ''} pour vos dates
                </p>
                {isFiltered && (
                  <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                    ✓ Règles de filtrage appliquées
                  </Badge>
                )}
              </div>
              {totalPages > 1 && (
                <p className="text-sm text-gray-500">
                  Affichage de {startIndex + 1} à {Math.min(endIndex, sortedVillas.length)} sur {sortedVillas.length}
                </p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-4 mt-4 md:mt-0">
            {/* View Toggle */}
            <div className="flex bg-gray-100 rounded-lg p-1">
              <Button
                variant={viewMode === 'grid' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('grid')}
                className="p-2"
              >
                <Grid3X3 className="w-4 h-4" />
              </Button>
              <Button
                variant={viewMode === 'list' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('list')}
                className="p-2"
              >
                <List className="w-4 h-4" />
              </Button>
            </div>

            {/* Sort */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="p-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 bg-white"
            >
              <option value="relevance">Pertinence</option>
              <option value="price-low">Prix croissant</option>
              <option value="price-high">Prix décroissant</option>
              <option value="name">Nom A-Z</option>
            </select>
          </div>
        </div>

        {/* Active Filters */}
        {(filters.destination || filters.emotion || filters.minPrice || filters.bedrooms) && (
          <div className="flex flex-wrap gap-2 mb-6">
            {filters.destination && (
              <Badge 
                variant="secondary" 
                className="flex items-center gap-2 px-3 py-1"
              >
                <MapPin className="w-3 h-3" />
                {filters.destination}
                <button 
                  onClick={() => handleFilterChange('destination', '')}
                  className="ml-1 hover:bg-gray-200 rounded-full p-0.5"
                >
                  <X className="w-3 h-3" />
                </button>
              </Badge>
            )}
            {filters.emotion && (
              <Badge 
                variant="secondary" 
                className="flex items-center gap-2 px-3 py-1"
              >
                <Heart className="w-3 h-3" />
                Émotion filtrée
                <button 
                  onClick={() => handleFilterChange('emotion', null)}
                  className="ml-1 hover:bg-gray-200 rounded-full p-0.5"
                >
                  <X className="w-3 h-3" />
                </button>
              </Badge>
            )}
          </div>
        )}

        {/* Loading State */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-16">
            <Loader2 className="w-12 h-12 text-amber-600 animate-spin mb-4" />
            <p className="text-gray-600">Chargement des villas...</p>
          </div>
        ) : error ? (
          <div className="text-center py-16">
            <div className="mb-6">
              <X className="w-16 h-16 text-red-300 mx-auto mb-4" />
              <h3 className="text-xl text-gray-600 mb-2">Erreur</h3>
              <p className="text-gray-500">{error}</p>
            </div>
            <Button 
              onClick={() => window.location.reload()}
              className="bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 text-white"
            >
              Réessayer
            </Button>
          </div>
        ) : sortedVillas.length > 0 ? (
          <>
            <div className={`grid gap-8 ${
              viewMode === 'grid' 
                ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3' 
                : 'grid-cols-1'
            }`}>
              {paginatedVillas.map((villa) => (
                <VillaCard
                  key={villa.id}
                  villa={villa}
                  onViewDetails={handleViewVilla}
                  onToggleFavorite={handleToggleFavorite}
                  isFavorite={favorites.has(villa.id)}
                  availabilityStatus={availabilityStatus[villa.id]}
                  layout={viewMode}
                />
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center items-center gap-2 mt-12">
                <Button
                  variant="outline"
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="px-4 py-2"
                >
                  Précédent
                </Button>
                
                <div className="flex items-center gap-2">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    
                    return (
                      <Button
                        key={pageNum}
                        variant={currentPage === pageNum ? 'default' : 'outline'}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`w-10 h-10 ${
                          currentPage === pageNum 
                            ? 'bg-gradient-to-r from-amber-600 to-amber-700 text-white' 
                            : ''
                        }`}
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                </div>

                <Button
                  variant="outline"
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  className="px-4 py-2"
                >
                  Suivant
                </Button>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-16">
            <div className="mb-6">
              <Search className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl text-gray-600 mb-2">Aucune villa trouvée</h3>
              <p className="text-gray-500">
                Essayez de modifier vos critères de recherche ou explorez nos destinations populaires
              </p>
            </div>
            <Button 
              onClick={clearFilters}
              className="bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 text-white"
            >
              Réinitialiser la recherche
            </Button>
          </div>
        )}
      </main>
    </div>
  );
};

export default SearchPage;